// The key a logged set carries, so a retry cannot record it twice.
//
// The server stores it with the set and answers a repeat with the stored set
// instead of a second copy (PERF-13). That only helps if a retry sends the SAME
// key. A key made fresh on every tap is a new set every tap, which is exactly
// the duplicate this exists to prevent. So a key belongs to one set with one
// set of inputs. It is kept across retries and replaced when the athlete moves
// on or changes what they typed.

/**
 * A v4 UUID.
 *
 * crypto.randomUUID is missing on iOS before 15.4, and in any page not served
 * over HTTPS (a phone on the LAN hitting the dev box by IP). getRandomValues
 * has neither restriction. Math.random is the last resort: the key only has to
 * be unique among one athlete's sets, not unguessable.
 */
export function newClientId(cryptoImpl = typeof window !== 'undefined' ? window.crypto : undefined) {
  if (cryptoImpl && typeof cryptoImpl.randomUUID === 'function') {
    return cryptoImpl.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (cryptoImpl && typeof cryptoImpl.getRandomValues === 'function') {
    cryptoImpl.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Version 4, RFC 4122 variant: the server checks the shape, not the version.
  // Stamping both keeps these indistinguishable from randomUUID's.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The key for one row of a multi-set form, made on first use and kept until
 * forgotten.
 *
 * `keys` is a Map the screen holds across renders. Forget a row's key when its
 * inputs change: an edited set is a different set. Clear the whole map once
 * everything has saved.
 */
export function keyFor(keys, rowId, make = newClientId) {
  if (!keys.has(rowId)) keys.set(rowId, make());
  return keys.get(rowId);
}
